import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, X } from "lucide-react";
import { AtomLoader } from "./AtomLoader";

/**
 * Visual banner shown while a generation or refinement job is still running
 * in the background. Used both for live in-tab progress AND for resumed jobs
 * after a tab refresh / close-and-reopen.
 *
 * Phase 104 (Feb 2026 — Generator Timeout UI Fix):
 * For the `generate` variant, after 45s of elapsed time the banner
 * calmly swaps to a "this is taking a little longer than usual" message
 * with a primary CTA to "Go to My Library →". The container stays the
 * same — same mint border, same loader icon — so it never reads as an
 * error state. Per the spec: no red, no warning icons, no language
 * suggesting something went wrong.
 */
const SLOW_THRESHOLD_SECONDS = 45;

export const LessonInProgressBanner = ({
  variant = "generate",
  startedAt,
  onDismiss,
}) => {
  // Phase 104 — drive elapsed-seconds via a 1-second interval that
  // SETS state from inside the callback (React-19 purity rule keeps
  // Date.now() out of render). On mount we compute the initial value
  // lazily via the useState initialiser; subsequent updates happen in
  // the interval callback only.
  const [elapsed, setElapsed] = useState(() =>
    startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : null,
  );
  useEffect(() => {
    if (!startedAt) {
      return undefined;
    }
    // Reset on startedAt change.
    const recompute = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    const id = window.setInterval(recompute, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const isSlow =
    variant === "generate" &&
    elapsed !== null &&
    elapsed >= SLOW_THRESHOLD_SECONDS;

  const baseCopy =
    variant === "refine"
      ? {
          title: "Refining your lesson…",
          body:
            "We're updating the lesson with your latest message — give it a few seconds.",
        }
      : {
          title: "Your lesson is still cooking.",
          body:
            "Shaping a full Ontario-aligned lesson — usually a minute or two. We'll drop it on screen the moment it's ready.",
        };

  const slowCopy = {
    title: "This one is taking a little longer than usual.",
    body:
      "Your lesson is still generating — it will appear in your Library when it's ready. You can stay here or navigate away; it won't affect the result.",
  };

  const copy = isSlow ? slowCopy : baseCopy;
  const isSubtle = variant === "refine";

  // Path B-3 (2026-02, founder ruling): the AtomLoader is the shared
  // face of every generate-and-wait moment. We keep the surrounding
  // banner chrome (context copy, elapsed timer, slow-state Library CTA)
  // and swap only the spinner atom-icon so the animated brand element
  // is consistent across the app. The outer testids stay intact for
  // downstream selectors.
  const useAtom = !isSubtle;

  return (
    <div
      data-testid={
        isSlow ? "lesson-progress-slow" : `lesson-progress-${variant}`
      }
      className={`relative rounded-xl border ${
        isSubtle
          ? "border-[#4a6984]/15 bg-white"
          : "border-[#96ded1]/60 bg-[#96ded1]/15 shadow-sm shadow-[#4a6984]/5"
      } p-5 md:p-6`}
    >
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
        {useAtom ? (
          <div className="shrink-0 w-full sm:w-auto flex justify-center">
            <AtomLoader
              size="sm"
              message={variant === "refine" ? "Refining" : "Building"}
            />
          </div>
        ) : (
          <div
            className={`h-10 w-10 rounded-lg grid place-items-center ${
              isSubtle
                ? "bg-[#f4f5f7] border border-[#4a6984]/15"
                : "bg-white border border-[#96ded1]/60"
            }`}
          >
            <Loader2 className="h-4 w-4 text-[#1c2d3d] animate-spin" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p
            data-testid={isSlow ? "lesson-progress-slow-title" : undefined}
            className="font-extrabold text-[#1c2d3d] tracking-tight"
          >
            {copy.title}
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85 leading-relaxed">
            {copy.body}
          </p>
          {elapsed !== null && !isSlow && (
            <p className="mt-2 text-xs font-semibold text-[#4a6984]/60">
              Elapsed: {elapsed}s
            </p>
          )}
          {isSlow && (
            <div className="mt-4">
              <Link
                to="/dashboard?view=library"
                data-testid="lesson-progress-go-to-library"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#96ded1] text-[#1c2d3d] font-bold text-sm hover:bg-[#7bcbc0] transition"
              >
                Go to My Library
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid={`lesson-progress-${variant}-dismiss`}
            aria-label="Dismiss"
            className="text-[#4a6984]/50 hover:text-[#4a6984] p-1"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LessonInProgressBanner;
